import type { CaptureResult, PostHogConfig, Properties } from "posthog-js";

const ABSOLUTE_URL = /^https?:\/\//;
const RECORD_ID = /\/\d+(?=[/?#]|$)/g;
const HISTORY_SECTION = /\/dashboard\/history\/[^/?#]+/g;
// In a path the @ and + arrive percent-encoded as often as not (/jane%40x.com, /%2B15551234567).
const EMAIL = /[\w.%+-]+(?:@|%40)[\w-]+(?:\.[\w-]+)*\.[a-z]{2,}/gi;
const PHONE = /(?<![\w%])(?:\+|%2B)?\d{10,15}(?!\w)|(?<![\w.-])\d{3}[.-]\d{3}[.-]\d{4}(?![\w.-])/gi;

// Walks nested values too: web vitals events repeat the page URL inside each metric.
const rewriteStrings = (properties: Properties | undefined, rewrite: (value: string) => string) => {
  if (!properties) {
    return;
  }

  for (const [key, value] of Object.entries(properties)) {
    if (typeof value === "string") {
      properties[key] = rewrite(value);
    } else if (value && typeof value === "object") {
      rewriteStrings(value, rewrite);
    }
  }
};

const rewriteEvent = (event: CaptureResult, rewrite: (value: string) => string) => {
  rewriteStrings(event.properties, rewrite);
  rewriteStrings(event.$set, rewrite);
  rewriteStrings(event.$set_once, rewrite);
};

const isUrlOrPath = (value: string) => ABSOLUTE_URL.test(value) || value.startsWith("/");

const withoutQuery = (value: string) => (isUrlOrPath(value) ? value.split(/[?#]/)[0] : value);

const withoutContactDetails = (value: string) =>
  isUrlOrPath(value) ? value.replace(EMAIL, ":email").replace(PHONE, ":phone") : value;

const withoutRecordIds = (value: string) =>
  isUrlOrPath(value) ? value.replace(RECORD_ID, "/:id") : value;

const withoutHistorySection = (value: string) =>
  isUrlOrPath(value) ? value.replace(HISTORY_SECTION, "/dashboard/history/:section") : value;

// Our query strings can hold names, emails, phone numbers and sign-in tokens, and admin page
// titles hold the claimant's name, so neither leaves the browser on any event. An email address or
// phone number typed or pasted into the address bar lands in the path of a 404, so it is masked too.
export const sanitizeEvent = (event: CaptureResult | null): CaptureResult | null => {
  if (!event) {
    return event;
  }

  rewriteEvent(event, withoutQuery);
  rewriteEvent(event, withoutContactDetails);
  delete event.properties.title;

  return event;
};

// Admin URLs name the claimant being worked on (/admin/profiles/123). Masking the ID keeps staff
// workflow analytics without tying any event, or the status it records, to a claimant.
export const maskRecordIds = (event: CaptureResult | null): CaptureResult | null => {
  if (!event) {
    return event;
  }

  rewriteEvent(event, withoutRecordIds);

  return event;
};

// History page addresses name what a claimant is recording (/dashboard/history/medication,
// /dashboard/history/marriage). Next to an "Added History" event, that says they take a medication
// or were married, so claimant events keep only that it was a history page.
export const maskHistorySection = (event: CaptureResult | null): CaptureResult | null => {
  if (!event) {
    return event;
  }

  rewriteEvent(event, withoutHistorySection);

  return event;
};

// Shared by the claimant and admin projects. Set in code so PostHog's project settings can't turn
// back on anything that records what is on screen (HCSC MSR §9). We use no PostHog feature flags,
// and the /flags request bypasses before_send and carries the entry URL with its query string.
export const POSTHOG_PRIVACY_CONFIG: Partial<PostHogConfig> = {
  autocapture: false,
  rageclick: false,
  capture_heatmaps: false,
  capture_dead_clicks: false,
  disable_session_recording: true,
  advanced_disable_flags: true,
  before_send: sanitizeEvent,
};

export const CLAIMANT_POSTHOG_PRIVACY_CONFIG: Partial<PostHogConfig> = {
  ...POSTHOG_PRIVACY_CONFIG,
  before_send: [sanitizeEvent, maskHistorySection],
};

export const ADMIN_POSTHOG_PRIVACY_CONFIG: Partial<PostHogConfig> = {
  ...POSTHOG_PRIVACY_CONFIG,
  before_send: [sanitizeEvent, maskRecordIds],
};
